import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

export function GerenciarCadastroB3Modal({ onClose, onSuccess }) {
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Estados do formulário
  const [empresa, setEmpresa] = useState('');
  const [codigo, setCodigo] = useState('');
  const [setor, setSetor] = useState('');
  const [categoria, setCategoria] = useState('');
  
  // Estados de edição
  const [editingId, setEditingId] = useState(null);
  const [editingData, setEditingData] = useState({});

  useEffect(() => {
    carregarEmpresas();
  }, []);

  // Preencher automaticamente "Imobiliário" quando categoria for "FII's"
  useEffect(() => {
    if (categoria === "FII's") {
      setSetor('Imobiliário');
    }
  }, [categoria]);

  const carregarEmpresas = async () => {
    try {
      const response = await axios.get(`${API}/empresas/B3`, getAuthHeaders());
      setEmpresas(response.data);
    } catch (error) {
      toast.error('Erro ao carregar empresas');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!empresa || !codigo || !setor || !categoria) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    try {
      if (editingId) {
        // Atualizar empresa existente
        await axios.put(`${API}/empresas/${editingId}`, {
          empresa,
          codigo,
          setor,
          categoria
        }, getAuthHeaders());
        toast.success('Empresa atualizada com sucesso');
        setEditingId(null);
      } else {
        // Criar nova empresa
        await axios.post(`${API}/empresas`, {
          template: 'B3',
          empresa,
          codigo,
          setor,
          categoria
        }, getAuthHeaders());
        toast.success('Empresa cadastrada com sucesso');
      }
      
      // Limpar formulário
      setEmpresa('');
      setCodigo('');
      setSetor('');
      setCategoria('');
      
      // Recarregar lista
      carregarEmpresas();
      
      if (onSuccess) onSuccess();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Empresa já cadastrada');
      } else {
        toast.error('Erro ao salvar empresa');
      }
    }
  };

  const handleEdit = (emp) => {
    setEditingId(emp.id);
    setEmpresa(emp.empresa);
    setCodigo(emp.codigo);
    setSetor(emp.setor);
    setCategoria(emp.categoria || '');
    
    // Scroll para o formulário
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEmpresa('');
    setCodigo('');
    setSetor('');
    setCategoria('');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta empresa?')) {
      return;
    }

    try {
      await axios.delete(`${API}/empresas/${id}`, getAuthHeaders());
      toast.success('Empresa excluída com sucesso');
      carregarEmpresas();
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error('Erro ao excluir empresa');
    }
  };

  const handleMetaChange = (empresaId, field, value) => {
    setEditingData(prev => {
      const newData = {
        ...prev,
        [empresaId]: {
          ...prev[empresaId],
          [field]: value
        }
      };
      
      // Lógica mutuamente exclusiva: se um campo foi preenchido, limpar o outro
      if (field === 'meta_basica' && value > 0) {
        newData[empresaId].meta_desejo = 0;
      } else if (field === 'meta_desejo' && value > 0) {
        newData[empresaId].meta_basica = 0;
      }
      
      return newData;
    });
  };

  // Função auxiliar para obter o valor atual do campo (considerando edição ou valor original)
  const getFieldValue = (emp, field) => {
    if (editingData[emp.id] && editingData[emp.id][field] !== undefined) {
      return editingData[emp.id][field];
    }
    return emp[field] || '';
  };

  // Função para verificar se um campo deve ser desabilitado
  const isFieldDisabled = (emp, field) => {
    const oppositeField = field === 'meta_basica' ? 'meta_desejo' : 'meta_basica';
    const oppositeValue = getFieldValue(emp, oppositeField);
    return oppositeValue > 0;
  };

  const handleSaveMeta = async (empresaId) => {
    const data = editingData[empresaId];
    if (!data) return;

    try {
      await axios.put(`${API}/empresas/${empresaId}`, data, getAuthHeaders());
      toast.success('Meta atualizada com sucesso');
      carregarEmpresas();
      
      // Limpar edição
      setEditingData(prev => {
        const newData = { ...prev };
        delete newData[empresaId];
        return newData;
      });
    } catch (error) {
      toast.error('Erro ao atualizar meta');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 rounded-t-2xl z-10">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <svg className="w-7 h-7 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              Gerenciar Cadastro - B3
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Formulário */}
        <div className="p-6 border-b border-gray-200 bg-blue-50">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {editingId ? 'Editar Empresa/Fundo' : 'Cadastrar Nova Empresa/Fundo'}
          </h3>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Empresa *
              </label>
              <input
                type="text"
                value={empresa}
                onChange={(e) => setEmpresa(e.target.value)}
                required
                placeholder="Ex: Petrobras"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Código *
              </label>
              <input
                type="text"
                value={codigo}
                onChange={(e) => setCodigo(e.target.value.toUpperCase())}
                required
                placeholder="Ex: PETR4 ou HGLG11"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Categoria *
              </label>
              <select
                value={categoria}
                onChange={(e) => setCategoria(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Selecione...</option>
                <option value="Bluechips">Bluechips</option>
                <option value="Smallcaps">Smallcaps</option>
                <option value="FII's">FII's</option>
              </select>
              {categoria === "FII's" && (
                <p className="text-xs text-green-600 mt-1">
                  ℹ️ Setor preenchido automaticamente como "Imobiliário"
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Setor *
              </label>
              <input
                type="text"
                value={setor}
                onChange={(e) => setSetor(e.target.value)}
                required
                placeholder="Ex: Energia"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="md:col-span-2 flex gap-3 justify-end">
              {editingId && (
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancelar Edição
                </button>
              )}
              <button
                type="submit"
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium"
              >
                {editingId ? 'Atualizar' : 'Cadastrar'}
              </button>
            </div>
          </form>
        </div>

        {/* Tabela */}
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Empresas Cadastradas ({empresas.length})
          </h3>
          
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : empresas.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              Nenhuma empresa cadastrada
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Empresa</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Setor</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Categoria</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Meta QTD</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Meta Valor</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ações</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {empresas.map((emp) => (
                    <tr key={emp.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">{emp.empresa}</td>
                      <td className="px-4 py-3 text-sm font-mono text-gray-900">{emp.codigo}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{emp.setor}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          emp.categoria === 'Bluechips' ? 'bg-blue-100 text-blue-800' :
                          emp.categoria === 'Smallcaps' ? 'bg-green-100 text-green-800' :
                          'bg-purple-100 text-purple-800'
                        }`}>
                          {emp.categoria || 'N/A'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <input
                          type="number"
                          value={getFieldValue(emp, 'meta_basica')}
                          onChange={(e) => handleMetaChange(emp.id, 'meta_basica', parseInt(e.target.value) || 0)}
                          onBlur={() => handleSaveMeta(emp.id)}
                          disabled={isFieldDisabled(emp, 'meta_basica')}
                          placeholder="QTD"
                          className={`w-20 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            isFieldDisabled(emp, 'meta_basica') ? 'bg-gray-100 cursor-not-allowed opacity-50' : ''
                          }`}
                        />
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <input
                          type="number"
                          step="0.01"
                          value={getFieldValue(emp, 'meta_desejo')}
                          onChange={(e) => handleMetaChange(emp.id, 'meta_desejo', parseFloat(e.target.value) || 0)}
                          onBlur={() => handleSaveMeta(emp.id)}
                          disabled={isFieldDisabled(emp, 'meta_desejo')}
                          placeholder="R$"
                          className={`w-24 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            isFieldDisabled(emp, 'meta_desejo') ? 'bg-gray-100 cursor-not-allowed opacity-50' : ''
                          }`}
                        />
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(emp)}
                            className="text-blue-600 hover:text-blue-800 p-1"
                            title="Editar"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDelete(emp.id)}
                            className="text-red-600 hover:text-red-800 p-1"
                            title="Excluir"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
